import type { AxiosError } from 'axios';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Bot, User, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/context/AuthContext';
import { OAuthAuthorizationPrompt } from '@/components/mcp-chat/OAuthAuthorizationPrompt';
import { MessageRenderer } from './messages/MessageRenderer';
import { ResponseProjection } from '@/types/mcpTool';
import { CopilotInitializeResponse } from '@/types/copilot';
import { transformToolResultToActionCard, createErrorActionCard } from '@/utils/copilot/responseProjection';
import { copilotService } from '@/api/services/copilotService';
import type { ConversationMessage } from '@/api/services/copilotService';
import type { CopilotToolRegistry } from '@/types/copilot';
import {
  buildToolContextMap,
  ensureInitializationCache,
  getInitializationCache,
  sanitizeConversationId,
  setInitializationCache,
  type CopilotInitializationCache,
} from '@/components/copilot/mcp/initializationCache';
import { useToolSelection } from '@/components/copilot/mcp/useToolSelection';
import { ToolSelectionControl } from '@/components/copilot/mcp/ToolSelectionControl';
import { handleStreamEvent } from '@/components/copilot/mcp/streamEventHandler';
import { HttpClient } from '@/api/services/HttpClient';
import { MCPCopilotHeader } from '@/components/copilot/mcp/components/MCPCopilotHeader';
import { MCPCopilotEmptyState } from '@/components/copilot/mcp/components/MCPCopilotEmptyState';
import type { MCPCopilotProps, Message, StreamResponse, PlanStep, ExecutionDetail } from '@/components/copilot/mcp/chatTypes';
import { authClient } from '@/config/auth';

const resolveDisplayName = (value: unknown, fallback: string): string => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }

  if (value && typeof value === 'object') {
    const candidate =
      (value as any).name ??
      (value as any).action_name ??
      (value as any).display_name ??
      (value as any).title;

    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate;
    }
  }

  if (value !== undefined && value !== null) {
    return String(value);
  }

  return fallback;
};

/**
 * MCP-based Copilot component
 * Uses the MCP streaming API for real-time chat with OAuth support
 */
export const MCPCopilot: React.FC<MCPCopilotProps> = ({
  className = '',
  onClose,
  conversationId,
  onConversationCreated,
}) => {
  const navigate = useNavigate();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { getSessionToken } = useAuth();
  const getTokenRef = useRef<typeof getSessionToken>();
  const onConversationCreatedRef = useRef<typeof onConversationCreated>();

  const initializationCache = ensureInitializationCache();
  const normalizedIncomingConversationId = sanitizeConversationId(conversationId ?? null);
  const cachedConversationId = sanitizeConversationId(initializationCache?.conversationId ?? null);
  const initialConversationIdState = normalizedIncomingConversationId ?? cachedConversationId ?? null;
  const initialToolRegistry = initializationCache?.toolRegistry ?? null;
  const initialCtxVersion = initializationCache?.ctxVersion ?? null;
  const initialPageContext = initializationCache?.pageContext ?? null;

  // State management
  const [messages, setMessages] = useState<Message[]>(() => {
    try {
      const saved = localStorage.getItem('mcp-copilot-messages');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<string>('');
  const [sessionId] = useState(() => `mcp-session-${Date.now()}`);

  // Store tool context for matching tool results with response_projection
  const [toolContextMap, setToolContextMap] = useState<Map<string, ResponseProjection>>(
    () => buildToolContextMap(initialToolRegistry)
  );
  const initialStreamToken = initializationCache?.streamToken ?? initializationCache?.response?.stream_token ?? null;
  const initialStreamTokenExpiresAt =
    initializationCache?.streamTokenExpiresAt ?? initializationCache?.response?.stream_token_expires_at ?? null;
  const streamTokenRef = useRef<string | null>(initialStreamToken);
  const streamTokenExpiresAtRef = useRef<string | null>(initialStreamTokenExpiresAt);
  const getResponseProjection = useCallback(
    (key: string) => toolContextMap.get(key),
    [toolContextMap]
  );

  // Track if formatted_response was rendered (to prevent duplicate display from 'complete' event)
  // Using useRef instead of useState for synchronous updates (avoids race condition)
  const hasRenderedFormattedResponseRef = useRef(false);

  // Copilot initialization state
  const [copilotProfile, setCopilotProfile] = useState<CopilotInitializeResponse | null>(
    initializationCache?.response ?? null
  );
  const [isInitializing, setIsInitializing] = useState(!initializationCache);
  const [initError, setInitError] = useState<string | null>(null);

  const {
    selectedToolIds,
    selectAll: selectAllTools,
    deselectAll: deselectAllTools,
    setSelection: setToolSelection,
  } = useToolSelection(copilotProfile?.allowed_tools);

  // Conversation/context state
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(initialConversationIdState);
  const [_ctxVersion, setCtxVersion] = useState<string | null>(initialCtxVersion);
  const ctxVersionRef = useRef<string | null>(initialCtxVersion);
  const currentConversationIdRef = useRef<string | null>(initialConversationIdState);
  const latestToolRegistryRef = useRef<CopilotToolRegistry | null>(initialToolRegistry);
  const latestPageContextRef = useRef<Record<string, any> | null>(initialPageContext);
  const isStreamingRef = useRef(false);
  const isStartingConversationRef = useRef(false);

  // Message queue state
  const [messageQueue, setMessageQueue] = useState<string[]>([]);
  const isProcessingRef = useRef(false);

  // MCP API endpoint
  const mcpApiUrl = import.meta.env.VITE_MCP_API_URL || 'https://beta-ai.pullseai.com';

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    setTimeout(() => {
      if (scrollAreaRef.current) {
        const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
        if (viewport) {
          viewport.scrollTop = viewport.scrollHeight;
        } else {
          scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
        }
      }
    }, 100);
  };

  // Keep local conversation ID in sync with parent prop
  useEffect(() => {
    const normalized = sanitizeConversationId(conversationId ?? null);
    setCurrentConversationId(prev => {
      if (prev === normalized) {
        return prev;
      }
      return normalized;
    });
  }, [conversationId]);

  useEffect(() => {
    currentConversationIdRef.current = currentConversationId;
  }, [currentConversationId]);

  useEffect(() => {
    const cache = getInitializationCache();
    if (!cache) {
      return;
    }

    const normalized = sanitizeConversationId(currentConversationId);
    if (cache.conversationId !== normalized) {
      const updatedCache: CopilotInitializationCache = {
        ...cache,
        conversationId: normalized,
        response: {
          ...cache.response,
          conversation_id: normalized ?? undefined,
        },
      };
      setInitializationCache(updatedCache);
    }
  }, [currentConversationId]);

  useEffect(() => {
    getTokenRef.current = getSessionToken;
  }, [getSessionToken]);

  useEffect(() => {
    onConversationCreatedRef.current = onConversationCreated;
  }, [onConversationCreated]);

  // Auto-scroll when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Persist messages to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('mcp-copilot-messages', JSON.stringify(messages));
    } catch (error) {
      console.error('Failed to persist MCP copilot messages:', error);
    }
  }, [messages]);

  // Clear chat function
  const clearChat = useCallback(() => {
    setMessages([]);
    try {
      localStorage.removeItem('mcp-copilot-messages');
    } catch (error) {
      console.error('Failed to clear persisted MCP copilot messages:', error);
    }
  }, []);

  // Expose clearChat globally
  useEffect(() => {
    // Add prominent console identifier to verify which component is running
    console.log('%c🚀 MCPCopilot NEW IMPLEMENTATION LOADED', 'background: #4ade80; color: #000; font-size: 16px; font-weight: bold; padding: 8px;');
    console.log('%c✅ If you see confidence/safety text in messages, you have a browser cache issue!', 'background: #fbbf24; color: #000; font-size: 12px; padding: 4px;');
    console.log('%c📖 See CACHE_CLEAR_INSTRUCTIONS.md for help', 'color: #60a5fa; font-size: 12px;');

    (window as any).clearCopilotChat = clearChat;
    (window as any).scrollCopilotToBottom = scrollToBottom;
    (window as any).__COPILOT_VERSION__ = 'MCP_v2.0';

    return () => {
      delete (window as any).clearCopilotChat;
      delete (window as any).scrollCopilotToBottom;
      delete (window as any).__COPILOT_VERSION__;
    };
  }, []);

  // Build a lookup map from tool registry context entries
  const applyToolRegistryToMap = useCallback((registry: CopilotToolRegistry | null | undefined) => {
    const newToolContextMap = buildToolContextMap(registry);
    setToolContextMap(newToolContextMap);
    latestToolRegistryRef.current = registry ?? null;

    const cache = getInitializationCache();
    if (cache) {
      const updatedCache: CopilotInitializationCache = {
        ...cache,
        toolRegistry: registry ?? null,
        response: {
          ...cache.response,
          tool_registry: registry ?? null,
        },
      };
      setInitializationCache(updatedCache);
    }
  }, []);

  const updateStreamTokenState = useCallback(
    (token: string | null, expiresAt?: string | null) => {
      const normalizedToken = token ?? null;
      const normalizedExpiry = expiresAt ?? null;
      streamTokenRef.current = normalizedToken;
      streamTokenExpiresAtRef.current = normalizedExpiry;

      const cache = getInitializationCache();
      if (cache) {
        setInitializationCache({
          ...cache,
          streamToken: normalizedToken,
          streamTokenExpiresAt: normalizedExpiry,
          response: {
            ...cache.response,
            stream_token: normalizedToken ?? undefined,
            stream_token_expires_at: normalizedExpiry ?? undefined,
          },
        });
      }
    },
    [getInitializationCache, setInitializationCache]
  );

  const ensureActiveStreamToken = useCallback(
    async (conversationId?: string | null): Promise<string> => {
      const tokenResponse = await copilotService.createStreamToken(conversationId || undefined);
      if (!tokenResponse.stream_token) {
        throw new Error('Unable to obtain stream token.');
      }

      updateStreamTokenState(tokenResponse.stream_token, tokenResponse.stream_token_expires_at ?? null);
      return tokenResponse.stream_token;
    },
    [copilotService, updateStreamTokenState]
  );

  const updateCtxVersionState = useCallback((nextVersion: string | null | undefined) => {
    const normalized = nextVersion ?? null;
    ctxVersionRef.current = normalized;
    setCtxVersion(prev => (prev === normalized ? prev : normalized));
    const cache = getInitializationCache();
    if (cache) {
      const updatedCache: CopilotInitializationCache = {
        ...cache,
        ctxVersion: normalized,
        response: {
          ...cache.response,
          ctx_version: normalized ?? undefined,
        },
      };
      setInitializationCache(updatedCache);
    }
  }, []);

  const startNewConversation = useCallback(async () => {
    if (isStartingConversationRef.current) {
      return currentConversationIdRef.current;
    }

    const activeInitialization = getInitializationCache() || ensureInitializationCache();
    const activeProfileResponse = copilotProfile || activeInitialization?.response || null;

    if (!activeProfileResponse) {
      throw new Error('Copilot profile unavailable. Please reload the copilot.');
    }

    const profileId = activeProfileResponse.copilot_profile?.id;
    if (!profileId) {
      throw new Error('Copilot profile ID is missing.');
    }

    const getTokenFn = getTokenRef.current;
    if (!getTokenFn) {
      throw new Error('No authentication mechanism available');
    }

    isStartingConversationRef.current = true;
    setStatus('Starting new chat...');

    try {
      // const token = await getTokenFn();
      // if (!token) {
      //   throw new Error('No authentication token available');
      // }

      const response = await copilotService.createConversation({
        copilot_profile_id: profileId,
        page_url: window.location.href,
        title: 'New Chat'
      });

      const newConversationId = sanitizeConversationId(response.conversation?.id ?? null);
      const newStreamToken = response.stream_token ?? null;
      const newStreamTokenExpiry = response.stream_token_expires_at ?? null;

      clearChat();
      setMessageQueue([]);
      hasRenderedFormattedResponseRef.current = false;
      updateCtxVersionState(null);
      latestPageContextRef.current = null;

      const updatedResponse: CopilotInitializeResponse = {
        ...activeProfileResponse,
        conversation_id: newConversationId ?? undefined,
        conversation: response.conversation ?? null,
        conversation_created: true,
        ctx_version: undefined,
        tool_registry: latestToolRegistryRef.current ?? null,
        page_context: null,
        stream_token: newStreamToken ?? undefined,
        stream_token_expires_at: newStreamTokenExpiry ?? undefined,
      };

      const updatedCache: CopilotInitializationCache = {
        response: updatedResponse,
        conversationId: newConversationId,
        ctxVersion: null,
        toolRegistry: latestToolRegistryRef.current ?? null,
        pageContext: null,
        streamToken: newStreamToken,
        streamTokenExpiresAt: newStreamTokenExpiry,
      };

      setInitializationCache(updatedCache);
      updateStreamTokenState(newStreamToken, newStreamTokenExpiry);
      setCopilotProfile(updatedResponse);
      applyToolRegistryToMap(updatedCache.toolRegistry);

      setCurrentConversationId(newConversationId);
      currentConversationIdRef.current = newConversationId;

      if (newConversationId && onConversationCreatedRef.current) {
        onConversationCreatedRef.current(newConversationId);
      }

      setStatus('');
      return newConversationId;
    } catch (error) {
      console.error('❌ [MCPCopilot] Failed to start new conversation:', error);
      setStatus('');
      throw error;
    } finally {
      isStartingConversationRef.current = false;
    }
  }, [applyToolRegistryToMap, clearChat, copilotProfile, updateCtxVersionState, updateStreamTokenState]);

  // Initialize copilot with profile and tools
  useEffect(() => {
    let isCancelled = false;

    const initializeCopilot = async () => {
      setInitError(null);

      const normalizedPropConversationId = sanitizeConversationId(conversationId ?? null);
      const normalizedRefConversationId = sanitizeConversationId(currentConversationIdRef.current ?? null);
      const cachedInitialization = getInitializationCache() || ensureInitializationCache();
      const normalizedCachedConversationId = sanitizeConversationId(cachedInitialization?.conversationId ?? null);

      const effectiveConversationId =
        normalizedPropConversationId ||
        normalizedRefConversationId ||
        normalizedCachedConversationId ||
        undefined;

      const canReuseCache =
        cachedInitialization &&
        normalizedCachedConversationId &&
        effectiveConversationId &&
        normalizedCachedConversationId === effectiveConversationId;

      if (canReuseCache) {
        console.log('♻️ [MCPCopilot] Using cached initialization data');
        setCopilotProfile(cachedInitialization.response);
        if (normalizedCachedConversationId) {
          setCurrentConversationId(prev => (prev === normalizedCachedConversationId ? prev : normalizedCachedConversationId));
          currentConversationIdRef.current = normalizedCachedConversationId;
        }
        updateCtxVersionState(cachedInitialization.ctxVersion);
        applyToolRegistryToMap(cachedInitialization.toolRegistry);
        latestPageContextRef.current = cachedInitialization.pageContext ?? null;
        setIsInitializing(false);
        return;
      }

      setIsInitializing(true);

      try {
        console.log('🚀 [MCPCopilot] Initializing copilot...', { conversationId: effectiveConversationId });
        const response = await copilotService.initializeCopilot(effectiveConversationId);

        if (isCancelled) {
          return;
        }

        setCopilotProfile(response);

        // Initialize no longer returns conversation_id — it's created on first stream message
        // Keep any existing conversation_id (for page refresh / resume scenarios)
        const normalizedConversationId = sanitizeConversationId(
          response.conversation_id ??
          (typeof effectiveConversationId === 'string' ? effectiveConversationId : null)
        );

        if (normalizedConversationId) {
          setCurrentConversationId(normalizedConversationId);
          currentConversationIdRef.current = normalizedConversationId;
        }

        const updatedCache: CopilotInitializationCache = {
          response,
          conversationId: normalizedConversationId,
          ctxVersion: response.ctx_version ?? null,
          toolRegistry: response.tool_registry ?? null,
          pageContext: response.page_context ?? null,
          streamToken: response.stream_token ?? null,
          streamTokenExpiresAt: response.stream_token_expires_at ?? null,
        };

        setInitializationCache(updatedCache);
        updateStreamTokenState(response.stream_token ?? null, response.stream_token_expires_at ?? null);

        applyToolRegistryToMap(updatedCache.toolRegistry);
        latestPageContextRef.current = updatedCache.pageContext;
        updateCtxVersionState(updatedCache.ctxVersion);

        console.log('✅ [MCPCopilot] Copilot initialized:', {
          profile: response.copilot_profile.name,
          persona: response.copilot_profile.persona_name,
          tools: response.tool_count,
          allowed_tools: response.allowed_tools?.map(t => t.name).join(', '),
        });
      } catch (error: any) {
        if (isCancelled) {
          return;
        }

        console.error('❌ [MCPCopilot] Initialization failed:', error);
        const status = error?.originalError?.response?.status || error?.response?.status;

        if (status === 404) {
          setInitError('Copilot not found. Please verify your copilot configuration.');
        } else if (status === 400) {
          const serverMessage = error?.originalError?.response?.data?.message || error?.message;
          setInitError(serverMessage || 'Invalid copilot configuration response.');
        } else {
          setInitError(error instanceof Error ? error.message : 'Failed to initialize copilot');
        }

        setCopilotProfile(null);
        setCurrentConversationId(null);
        currentConversationIdRef.current = null;
        setInitializationCache(null);
        applyToolRegistryToMap(null);
        latestPageContextRef.current = null;
        updateCtxVersionState(null);
      } finally {
        if (!isCancelled) {
          setIsInitializing(false);
        }
      }
    };

    initializeCopilot();

    return () => {
      isCancelled = true;
    };
  }, [applyToolRegistryToMap, clearChat, conversationId, updateCtxVersionState, updateStreamTokenState]);

  useEffect(() => {
    (window as any).__startNewCopilotConversation = startNewConversation;

    return () => {
      if ((window as any).__startNewCopilotConversation === startNewConversation) {
        delete (window as any).__startNewCopilotConversation;
      }
    };
  }, [startNewConversation]);

  // Handle OAuth code submission
  const handleOAuthCodeSubmit = async (code: string) => {
    console.log('🔐 [MCPCopilot] Submitting OAuth code:', code);

    try {
      // const token = await getSessionToken();
      // if (!token) {
      //   throw new Error('No authentication token available');
      // }

      const response = await fetch(`${mcpApiUrl}/chat/mcp/oauth/code`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code,
          session_id: sessionId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit OAuth code: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ [MCPCopilot] OAuth code submitted successfully:', result);

      // Add success message
      const successMessage: Message = {
        id: `oauth-success-${Date.now()}`,
        role: 'assistant',
        content: 'Authorization successful! You can now retry your original request.',
        createdAt: new Date().toISOString(),
      };

      setMessages(prev => [...prev, successMessage]);
    } catch (error) {
      console.error('❌ [MCPCopilot] OAuth code submission failed:', error);

      const errorMessage: Message = {
        id: `oauth-error-${Date.now()}`,
        role: 'assistant',
        content: `Failed to submit authorization code: ${error instanceof Error ? error.message : 'Unknown error'}`,
        createdAt: new Date().toISOString(),
      };

      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const parseMetadata = useCallback((metadata: any): Record<string, any> | undefined => {
    if (!metadata) {
      return undefined;
    }

    if (typeof metadata === 'string') {
      try {
        return JSON.parse(metadata);
      } catch (error) {
        console.error('❌ [MCPCopilot] Failed to parse metadata JSON string:', metadata, error);
        return undefined;
      }
    }

    if (typeof metadata === 'object') {
      return metadata as Record<string, any>;
    }

    return undefined;
  }, []);

  const mapServerMessageToClient = useCallback(
    (serverMessage: ConversationMessage): Message[] => {
      const metadata = parseMetadata(serverMessage.metadata);
      const structuredDataPayload =
        metadata?.structured_data ??
        (serverMessage as any)?.structured_data ??
        (serverMessage as any)?.structured_payloads?.structured_data;
      const actionCardDataPayload =
        metadata?.action_card_data ??
        metadata?.actionCardData ??
        (serverMessage as any)?.action_card_data;

      const resolveRole = (): Message['role'] => {
        if (actionCardDataPayload) {
          return 'tool_result';
        }
        if (structuredDataPayload?.type === 'tool_result') {
          return 'assistant';
        }
        if (metadata?.type === 'tool_result' || metadata?.structured_data?.type === 'tool_result') {
          return 'tool_result';
        }

        switch (serverMessage.role) {
          case 'user':
            return 'user';
          case 'system':
            return 'system';
          case 'tool_result':
            return 'tool_result';
          default:
            return 'assistant';
        }
      };

      const content =
        typeof serverMessage.content === 'string'
          ? serverMessage.content
          : serverMessage.content != null
            ? JSON.stringify(serverMessage.content)
            : '';

      const executionResultsPayload =
        (serverMessage as any)?.execution_results ??
        structuredDataPayload?.execution_results ??
        metadata?.execution_results ??
        (serverMessage as any)?.structured_payloads?.execution_results;

      // Build plan_steps from structured_data.plan (persisted by backend)
      const planRaw =
        structuredDataPayload?.plan ??
        metadata?.plan ??
        (serverMessage as any)?.plan;
      const planStepsPayload: PlanStep[] | undefined =
        (metadata?.plan_steps ?? structuredDataPayload?.plan_steps ?? (serverMessage as any)?.plan_steps) ||
        (Array.isArray(planRaw)
          ? planRaw.map((p: any, i: number) => ({
              name: p.description || p.step_id || `Step ${i + 1}`,
              description: p.description || '',
              status: 'completed' as const,
              tool_name: p.tool || undefined,
            }))
          : undefined);

      // Build execution_details from execution_results.steps (persisted by backend)
      let executionDetailsPayload: ExecutionDetail[] | undefined =
        metadata?.execution_details ??
        structuredDataPayload?.execution_details ??
        (serverMessage as any)?.execution_details;

      if (
        !executionDetailsPayload &&
        executionResultsPayload?.steps &&
        Array.isArray(executionResultsPayload.steps)
      ) {
        executionDetailsPayload = executionResultsPayload.steps
          .filter((step: any) => !!step)
          .map((step: any) => ({
            tool_name: resolveDisplayName(
              step.tool_name ?? step.tool?.name ?? step.result?.tool_name ?? step.result?.tool?.name ?? step.result?.tool,
              'Tool'
            ),
            action_name: resolveDisplayName(
              step.description ?? step.action_name ?? step.action?.name ?? step.result?.action_name ?? step.result?.action,
              'Action'
            ),
            raw_response:
              step.result?.response ??
              step.raw_response ??
              step.result?.result?.response ??
              step.structured_data?.raw_response,
          }));
      }

      const baseMessage: Message = {
        id: serverMessage.id,
        content,
        role: resolveRole(),
        createdAt: serverMessage.created_at ?? new Date().toISOString(),
        structured_data: structuredDataPayload,
        action_card_data: actionCardDataPayload,
        execution_details: executionDetailsPayload,
        plan_steps: planStepsPayload,
        metadata,
      };

      const normalizedMessages: Message[] = [];

      // When we have execution_details (extracted from execution_results),
      // always include the base message — its content is the LLM-synthesized
      // summary which is the primary display. The execution_details and
      // plan_steps render as supplementary collapsible UI.
      const hasExecutionDetails = executionDetailsPayload && executionDetailsPayload.length > 0;
      const shouldIncludeBaseMessage =
        hasExecutionDetails ||
        baseMessage.role !== 'assistant' ||
        Boolean(
          (baseMessage.content && baseMessage.content.trim().length > 0) ||
            baseMessage.structured_data ||
            baseMessage.action_card_data
        );

      if (shouldIncludeBaseMessage) {
        normalizedMessages.push(baseMessage);
      }

      // Only create per-step messages if we did NOT convert them to execution_details.
      // This handles action_card_data-only steps (no formatted_response/raw_response).
      if (!hasExecutionDetails && executionResultsPayload?.steps && Array.isArray(executionResultsPayload.steps)) {
        executionResultsPayload.steps.forEach((step: any, index: number) => {
          if (!step) return;

          const actionCardData =
            step.action_card_data ?? step.result?.action_card_data ?? step.result?.result?.action_card_data;

          if (actionCardData) {
            const toolName = resolveDisplayName(
              step.tool_name ?? step.tool?.name ?? step.result?.tool_name ?? step.result?.tool?.name ?? step.result?.tool,
              'Tool'
            );
            const actionName = resolveDisplayName(
              step.action_name ?? step.action?.name ?? step.result?.action_name ?? step.result?.action,
              'Action'
            );
            const createdAt =
              step.completed_at ?? step.finished_at ?? step.started_at ?? serverMessage.created_at ?? new Date().toISOString();
            const messageId = `${serverMessage.id}-execution-${step.id ?? index}`;

            normalizedMessages.push({
              id: `${messageId}-card`,
              role: 'tool_result',
              content: step.result?.message ?? `Executed: ${toolName}.${actionName}`,
              createdAt,
              action_card_data: actionCardData,
            });
          }
        });
      }

      return normalizedMessages;
    },
    [parseMetadata]
  );

  useEffect(() => {
    if (isInitializing) {
      return;
    }

    // Don't touch messages while actively streaming — the conversation event
    // from SSE sets currentConversationId mid-stream, which would trigger this
    // effect and overwrite the in-flight messages (including the user's message).
    if (isStreamingRef.current) {
      return;
    }

    if (!currentConversationId) {
      clearChat();
      return;
    }

    // Clear old messages and show loading while fetching the new conversation
    clearChat();
    setIsLoading(true);
    setStatus('Loading conversation...');

    let isCancelled = false;

    const loadConversationHistory = async () => {
      try {
        const response = await copilotService.getConversationMessages(currentConversationId, {
          limit: 200,
        });

        if (isCancelled) {
          return;
        }

        const mappedMessages = (response.messages ?? [])
          .sort((a, b) => (a.sequence_number ?? 0) - (b.sequence_number ?? 0))
          .flatMap(mapServerMessageToClient);

        setMessages(mappedMessages);

        if (mappedMessages.length > 0) {
          console.log(
            `🗂️ [MCPCopilot] Loaded ${mappedMessages.length} historical messages for conversation ${currentConversationId}`
          );
        } else {
          console.log(
            `🗂️ [MCPCopilot] No historical messages found for conversation ${currentConversationId}`
          );
        }
      } catch (error) {
        if (isCancelled) {
          return;
        }

        // 404/500 on conversation load = conversation doesn't exist in DB yet
        // (created lazily on first stream message) — silently ignore
        const status = (error as any)?.response?.status
          ?? (error as any)?.originalError?.response?.status
          ?? (error as any)?.status;
        if (status === 404 || status === 500) {
          console.log('📭 [MCPCopilot] Conversation not in DB yet (will be created on first message)');
        } else {
          console.error('❌ [MCPCopilot] Failed to load conversation history:', error);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
          setStatus('');
        }
      }
    };

    loadConversationHistory();

    return () => {
      isCancelled = true;
    };
  }, [clearChat, currentConversationId, isInitializing, mapServerMessageToClient]);

  // Process queue - execute the actual message sending
  useEffect(() => {
    if (isProcessingRef.current || messageQueue.length === 0 || isLoading) {
      return;
    }

    const processNextMessage = async () => {
      isProcessingRef.current = true;
      const nextMessage = messageQueue[0];

      try {
        await actualSendMessage(nextMessage);
      } finally {
        // Remove the processed message from queue
        setMessageQueue(prev => prev.slice(1));
        isProcessingRef.current = false;
      }
    };

    processNextMessage();
  }, [messageQueue, isLoading]);

  // Send message to MCP API (actual implementation)
  const actualSendMessage = async (content: string) => {
    if (!content.trim()) return;

    const activeConversationId = currentConversationIdRef.current;

    // Reset formatted response flag for new request
    hasRenderedFormattedResponseRef.current = false;
    console.log('🔄 [MCPCopilot] Reset hasRenderedFormattedResponse flag for new request');

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      content: content.trim(),
      role: 'user',
      createdAt: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    isStreamingRef.current = true;

    try {
      // const token = await getSessionToken();
      // if (!token) {
      //   throw new Error('No authentication token available');
      // }

      console.log('🚀 [MCPCopilot] Sending request to copilot chat endpoint');
      console.log('📝 Message:', userMessage.content);
      console.log('💬 Conversation ID:', activeConversationId);
      console.log('📍 Page URL:', window.location.href);

      const payload: Record<string, any> = {
        message: userMessage.content,
        page_url: window.location.href,
      };

      // Include conversation_id if we have one (for follow-up messages)
      if (activeConversationId) {
        payload.conversation_id = activeConversationId;
      }

      if (ctxVersionRef.current) {
        payload.ctx_version = ctxVersionRef.current;
      }

      payload.tool_ids = [...selectedToolIds];

      const activeStreamToken = await ensureActiveStreamToken(activeConversationId);
      if (!activeStreamToken) {
        throw new Error('No stream token available for this conversation.');
      }

      let buffer = '';
      let accumulatedContent = '';
      const assistantMessageId = `assistant-${Date.now()}`;
      let currentEvent = '';
      let lastProcessedIndex = 0;
      let hasLoggedConnection = false;

      const processChunk = (chunk: string) => {
        if (!chunk) {
          return;
        }

        buffer += chunk;
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) {
            currentEvent = '';
            continue;
          }

          let data: StreamResponse | null = null;

          if (line.startsWith('event: ')) {
            currentEvent = line.substring(7).trim();
            console.log('📡 [MCPCopilot] Event:', currentEvent);
            continue;
          }

          if (line.startsWith('data: ')) {
            const dataStr = line.substring(6).trim();
            if (!dataStr) {
              continue;
            }
            try {
              data = JSON.parse(dataStr);
            } catch (e) {
              console.error('❌ [MCPCopilot] Failed to parse SSE data:', dataStr, e);
              continue;
            }
          } else {
            try {
              data = JSON.parse(line);
            } catch {
              continue;
            }
          }

          if (data) {
            // Map SSE event name to data.type for the stream handler
            if (currentEvent && !data.type) {
              data.type = currentEvent as StreamResponse['type'];
            }
            // Map backend token format: {token: "word"} → {content: "word", type: "token"}
            if (currentEvent === 'token' && data.token && !data.content) {
              data.content = data.token;
            }
            // Map backend complete format: {response: "..."} → {content: "..."}
            if (currentEvent === 'complete' && data.response && !data.content) {
              data.content = data.response;
            }
            // Handle conversation event — backend created the conversation
            if (currentEvent === 'conversation' && data.conversation_id) {
              const newConvId = data.conversation_id;
              console.log('💬 [MCPCopilot] Conversation created by backend:', newConvId);
              setCurrentConversationId(newConvId);
              currentConversationIdRef.current = newConvId;
              if (onConversationCreatedRef.current) {
                onConversationCreatedRef.current(newConvId);
              }
              // Update cache with the real conversation_id
              const cache = getInitializationCache();
              if (cache) {
                setInitializationCache({
                  ...cache,
                  conversationId: newConvId,
                  response: { ...cache.response, conversation_id: newConvId },
                });
              }
              continue;
            }
            console.log('📦 [MCPCopilot] Received:', { event: currentEvent, data });
            const { accumulatedContent: nextContent } = handleStreamEvent({
              data,
              currentEvent,
              assistantMessageId,
              accumulatedContent,
              hasRenderedFormattedResponseRef,
              setMessages,
              setStatus,
              setIsLoading,
              updateCtxVersionState,
              handleToolRegistryUpdate: applyToolRegistryToMap,
              getResponseProjection,
              updateStreamToken: updateStreamTokenState,
            });

            accumulatedContent = nextContent;
          }
        }
      };
      const session = await authClient.getSession();
      const userId = session.data.user.id
      const workspaceId = localStorage.getItem("workspaceId");

      let axiosResponse;
      try {
        axiosResponse = await HttpClient.apiClient.post(
          `${mcpApiUrl}/copilot/chat/stream`,
          payload,
          {
            timeout: 0, // allow streaming connection to stay open
            headers: {
              // 'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'text/event-stream',
              'X-Stream-Token': activeStreamToken,
              "X-User-Id": userId,
              "X-Workspace-Id": workspaceId || "",
              "X-Role": "admin"
            },
            responseType: 'text',
            transitional: {
              forcedJSONParsing: false,
              silentJSONParsing: true,
            },
            onDownloadProgress: (progressEvent) => {
              const rawTarget = (progressEvent.event?.currentTarget ??
                progressEvent.event?.target) as XMLHttpRequest | null;

              if (!rawTarget || typeof rawTarget.responseText !== 'string') {
                return;
              }

              const responseText = rawTarget.responseText;
              if (responseText.length <= lastProcessedIndex) {
                return;
              }

              const nextChunk = responseText.substring(lastProcessedIndex);
              lastProcessedIndex = responseText.length;
              if (!hasLoggedConnection) {
                hasLoggedConnection = true;
                console.log('✅ [MCPCopilot] Connected to stream');
              }
              processChunk(nextChunk);
            },
          }
        );
      } catch (axiosError) {
        const typedError = axiosError as AxiosError;
        if (typedError?.response) {
          const status = typedError.response.status;
          const responseData = typedError.response.data as any;
          const statusText =
            typedError.response.statusText ||
            (typeof responseData === 'string' ? responseData : responseData?.message) ||
            'Request failed';
          throw new Error(`HTTP ${status}: ${statusText}`);
        }
        throw axiosError;
      }

      if (typeof axiosResponse?.data === 'string' && axiosResponse.data.length > lastProcessedIndex) {
        if (!hasLoggedConnection) {
          hasLoggedConnection = true;
          console.log('✅ [MCPCopilot] Connected to stream');
        }
        processChunk(axiosResponse.data.substring(lastProcessedIndex));
        lastProcessedIndex = axiosResponse.data.length;
      }

      if (buffer) {
        processChunk('\n');
      }
    } catch (error) {
      console.error('❌ [MCPCopilot] Error:', error);

      let friendlyContent = 'Unknown error occurred';

      if (error instanceof Error) {
        friendlyContent = error.message;
        const httpMatch = error.message.match(/HTTP\s(\d{3})/);

        if (httpMatch) {
          const statusCode = Number(httpMatch[1]);

          if (statusCode === 404) {
            friendlyContent = 'Conversation not found. Starting a new chat may be required.';
            setCurrentConversationId(null);
            updateCtxVersionState(null);
            ctxVersionRef.current = null;
            latestToolRegistryRef.current = null;
            setToolContextMap(new Map());
          } else if (statusCode === 400) {
            friendlyContent = 'Unable to process that request. Please try again.';
          }
        }
      }

      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: `Error: ${friendlyContent}`,
        createdAt: new Date().toISOString(),
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      isStreamingRef.current = false;
      setIsLoading(false);
      setStatus('');
    }
  };

  // Queue-based sendMessage wrapper - adds messages to queue instead of blocking
  const sendMessage = async (content: string) => {
    if (!content.trim()) return;

    console.log('📨 [MCPCopilot] Queueing message:', content);
    setMessageQueue(prev => [...prev, content]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const messageContent = input.trim();
    setInput('');
    await sendMessage(messageContent);
  };

  const handleFormSubmit = async (formData: Record<string, string>) => {
    console.log('📝 [MCPCopilot] Form submitted:', formData);

    const formText = Object.entries(formData)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');

    await sendMessage(formText);
  };

  const handleActionClick = async (action: string, itemId: string, itemData?: any) => {
    console.log('🔗 [MCPCopilot] Action clicked:', { action, itemId, itemData });

    let actionMessage = '';

    switch (action) {
      case 'view_ticket_details':
      case 'view_details':
        actionMessage = `View details for ${itemData?.title || 'item'} (ID: ${itemId})`;
        break;
      case 'assign_ticket_to_me':
        actionMessage = `Assign ticket "${itemData?.title || 'item'}" (ID: ${itemId}) to me`;
        break;
      default:
        actionMessage = `Perform action "${action}" on ${itemId}`;
    }

    await sendMessage(actionMessage);
  };

  return (
    <div className={`h-full flex flex-col copilot-kit-chat-container ${className}`}>
      <MCPCopilotHeader
        isInitializing={isInitializing}
        initError={initError}
        copilotProfile={copilotProfile}
        isLoading={isLoading}
        onClose={onClose}
      />

      {/* Messages area */}
      <ScrollArea className="flex-1 p-4" ref={scrollAreaRef}>
        <div className="space-y-4">
          <MCPCopilotEmptyState
            show={messages.length === 0}
            isInitializing={isInitializing}
            initError={initError}
            copilotProfile={copilotProfile}
          />

          {messages.map((message) => (
            <div key={message.id}>
              {message.role === 'oauth' && message.oauthData ? (
                <OAuthAuthorizationPrompt
                  tool={message.oauthData.tool}
                  action={message.oauthData.action}
                  authUrl={message.oauthData.authUrl}
                  message={message.oauthData.message}
                  onCodeSubmit={handleOAuthCodeSubmit}
                  actionRequired={message.oauthData.actionRequired}
                />
              ) : message.role === 'tool_result' && message.action_card_data ? (
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0 w-full">
                    <MessageRenderer
                      content={message.content}
                      role="assistant"
                      structured_data={message.structured_data}
                      action_card_data={message.action_card_data}
                      execution_details={message.execution_details}
                      plan_steps={message.plan_steps}
                      onLinkClick={(href) => {
                        if (href.startsWith('/')) {
                          navigate(href);
                        } else {
                          window.open(href, '_blank', 'noopener,noreferrer');
                        }
                      }}
                      onFormSubmit={handleFormSubmit}
                      onActionClick={handleActionClick}
                      onSuggestionClick={async (suggestion) => {
                        await sendMessage(suggestion);
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div
                  className={`flex items-start gap-3 ${
                    message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                  } ${message.role === 'assistant' && message.structured_data?.type === 'tool_result' ? 'w-full' : ''}`}
                >
                  <div className="flex-shrink-0">
                    {message.role === 'assistant' ? (
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <Bot className="h-4 w-4 text-primary" />
                      </div>
                    ) : (
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-gray-600" />
                      </div>
                    )}
                  </div>

                  <div
                    className={`min-w-0 p-3 rounded-lg ${
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground rounded-tr-sm max-w-[75%]'
                        : `bg-gray-100 text-gray-900 rounded-tl-sm ${
                            message.structured_data?.type === 'tool_result' ? 'w-full' : 'max-w-[75%]'
                          }`
                    }`}
                  >
                    <div className="text-sm leading-relaxed">
                      <MessageRenderer
                        content={message.content}
                        role={message.role}
                        structured_data={message.structured_data}
                        execution_details={message.execution_details}
                        plan_steps={message.plan_steps}
                        metadata={message.metadata}
                        onLinkClick={(href) => {
                          if (href.startsWith('/')) {
                            navigate(href);
                          } else {
                            window.open(href, '_blank', 'noopener,noreferrer');
                          }
                        }}
                        onFormSubmit={handleFormSubmit}
                        onActionClick={handleActionClick}
                        onSuggestionClick={async (suggestion) => {
                          await sendMessage(suggestion);
                        }}
                      />
                    </div>
                    <div
                      className={`text-xs mt-2 ${
                        message.role === 'user' ? 'text-primary-foreground/70' : 'text-gray-500'
                      }`}
                    >
                      {new Date(message.createdAt).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          {isLoading && (
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                <Bot className="h-4 w-4 text-primary" />
              </div>
              <div className="bg-gray-100 p-3 rounded-lg rounded-tl-sm">
                <div className="flex flex-col gap-2">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                  {status && (
                    <p className="text-xs text-gray-600">{status}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input area */}
      <div className="p-4 border-t border-gray-200 bg-white">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask me anything..."
            disabled={isLoading}
            className="flex-1"
          />
          <Button
            type="submit"
            disabled={isLoading || !input.trim()}
            className="bg-primary hover:bg-primary/90"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </form>
        <ToolSelectionControl
          tools={copilotProfile?.allowed_tools ?? []}
          selectedToolIds={selectedToolIds}
          onChange={setToolSelection}
          onSelectAll={selectAllTools}
          onDeselectAll={deselectAllTools}
        />
      </div>
    </div>
  );
};

export default MCPCopilot;
